import { RichText } from 'component/richtext';
import type { VideoVotingResult } from 'data/video-votings';
import { useVideoVoting as useVideoVotingByKey } from 'data/video-votings';
import { useVideo } from 'open-modules/zdf/component/videoplayer/videoplayer-state';
import { getVideoVotingSessionData, useVideoVotingSessionData } from 'open-modules/zdf/videovoting/videoVotingSession';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { trackEvent } from 'util/analytics';
import { type Datastore, useInstanceKey } from 'util/datastore';
import { t } from 'util/stdtypes';

import type { VideoVotingQuestion } from './videovoting-types';

export { useAllVideoVotings } from 'data/video-votings';

/**
 * The voting this screen is showing: the one named explicitly, else the one the
 * current session is playing, else the instance the screen is mounted on. Every
 * caller but the editor relies on one of the two fallbacks.
 */
export function useVideoVoting(votingKey?: string): VideoVotingResult | undefined {
    const session = useVideoVotingSessionData();
    const instanceKey = useInstanceKey();
    return useVideoVotingByKey(votingKey ?? session?.votingKey ?? instanceKey);
}

/** @public */
export function useSortedVideoQuestions(): VideoVotingQuestion[] {
    const voting = useVideoVoting();

    return useMemo(() => {
        return getSortedVideoQuestions(voting?.questions ?? []);
    }, [voting]);
}

function getSortedVideoQuestions(questions: VideoVotingQuestion[]) {
    return questions
        .map((q) => ({
            ...q,
            timestamp: q.timestamp === undefined ? 0 : q.timestamp,
        }))
        .toSorted((q1: VideoVotingQuestion, q2: VideoVotingQuestion) => {
            if (q1.timestamp !== undefined && q2.timestamp !== undefined) {
                return q1.timestamp - q2.timestamp; // Sort by timestamp
            }
            return 0; // Keep everything else as is
        });
}

export function useCurrentQuestion(): VideoVotingQuestion | null {
    const sortedQuestions = useSortedVideoQuestions();
    const video = useVideo();
    // Stabilize by key so consumers don't re-render every time
    // findQuestionForTimestamp returns a structurally-equal object pointing at
    // the same question.
    const currentQuestion = findQuestionForTimestamp(sortedQuestions, video.currentTime);
    const currentKey = currentQuestion?.key;
    return useMemo(() => sortedQuestions.find((q) => q.key === currentKey) ?? null, [sortedQuestions, currentKey]);
}

function findQuestionForTimestamp(questions: VideoVotingQuestion[], timestamp: number | null | undefined) {
    if (!questions || timestamp == null) return null;

    for (let i = 0; i < questions.length; i++) {
        const question = questions[i];

        if (question.timestamp !== undefined && timestamp >= question.timestamp) {
            // Timestamp must no be after the timestamp of the next question
            const nextQuestionIndex = i + 1;
            if (
                i === questions.length - 1 ||
                (questions[nextQuestionIndex].timestamp !== undefined && questions[nextQuestionIndex].timestamp > timestamp)
            ) {
                return question;
            }
        }
    }
    return null;
}

// Print the video voting session data to verify it using snapshots
export function VideoVotingSessionInformationBox() {
    const sessionState = useVideoVotingSessionData();

    return (
        <>
            {sessionState ? (
                <View>
                    <RichText text={'**State:** ' + sessionState.state}></RichText>
                    <RichText text={'**Current question key:** ' + sessionState.currentQuestionKey}></RichText>
                    <RichText text={'**Anonymous voting:** ' + sessionState.anonymous}></RichText>
                    <RichText text={'**Answered questions:** ' + JSON.stringify(sessionState.questions)}></RichText>
                </View>
            ) : (
                <RichText label={t('zdf_videovoting.no_session', '**No session initialized**')}></RichText>
            )}
        </>
    );
}

type logQuestionDialogEventParams = { datastore: Datastore; eventType: string; question: VideoVotingQuestion; data?: Record<string, any> };
export function logQuestionDialogEvent({ datastore, eventType, question, data }: logQuestionDialogEventParams) {
    const session = getVideoVotingSessionData(datastore);
    trackEvent(datastore, eventType, {
        from: 'question-dialog',
        questionKey: question.key,
        questionText: question.text,
        videoVotingSessionId: session.id,
        anonymous: session?.anonymous ? session.anonymous : false,
        ...data,
    });
}
